import { useState, useEffect, useCallback, useRef } from 'react';
import { Trash2, Clock, ExternalLink, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import PropTypes from 'prop-types';

import ConfirmDeleteModal from '../child/ConfirmDeleteModal';
import { deleteAssistant, saveAssistantDetail } from '../../store/app/assistantSlice';
import { useSelector } from 'react-redux';

import { getAllOrganisationData, getOrganisationDataWithId } from '../../utils/api/organisationAPI';
import { getGeminiResponse } from '../chatWidget/geminiClient';
import ChatWidget from '../chatWidget/ChatWidget';
import Vapi from '@vapi-ai/web';
import { Phone, PhoneOff } from 'lucide-react';
import WebCallVariableModal from './WebCallVariableModal';
import { getOrganisationIntegrations } from '../../utils/api/integrationAPI';
import { createSession } from '../../utils/api/sessionAPI';
import { chatApi } from '../../utils/api/chatApi';

function RightPanel({ assistantId }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const activeDetail = useSelector((state) => state.assistants.activeDetail);

  const updatedDetail = useSelector((state) => state.assistants.updateData);
  const [showVariableModal, setShowVariableModal] = useState(false);
  const dispatch = useDispatch();
  const [orgIntegration, setOrgIntegration] = useState(null);
  const wallet = useSelector((state) => state?.walletData?.walletData);
  //console.log("Wallet Data:", (wallet.creditsRemaining / 60).toFixed(2));

  const orgData = useSelector((state) => state.orgData.orgData);

  useEffect(() => {
    if (orgData?.organization?.id) {
      getOrganisationIntegrations(orgData.organization.id)
        .then((data) => {
          const apiKey = data?.integrations?.[0]?.external_api_public_key;
          if (apiKey) {
            setOrgIntegration(apiKey);
          } else {
            toast.error("No integration API key found.");
          }
        })
        .catch((error) => {
          console.error('Error fetching organisation integrations:', error);
          toast.error("Failed to load integration data.");
        });
    }
  }, [orgData]);


  const Spinner = () => (
    <span
      style={{
        width: '16px',
        height: '16px',
        marginRight: '0.5rem',
        border: '2px solid white',
        borderTop: '2px solid transparent',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        display: 'inline-block'
      }}
    />
  );


  const [showReassignModal, setShowReassignModal] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [startChat, setStartChat] = useState(false);
  const [startWebCall, setStartWebCall] = useState(false);
  const vapi = useRef(new Vapi(orgIntegration)).current;
  const [callDuration, setCallDuration] = useState(0);
  const [isStartWebCall, setIsStartWebCall] = useState(false);
  const userOrgId = useSelector((state) => state?.user?.user?.organizationId);
  const [currentSession, setCurrentSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);


  useEffect(() => {
    let timer;
    if (startWebCall) {
      timer = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(timer);
  }, [startWebCall]);

  const formatDuration = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  useEffect(() => {
    vapi.on("call-start", () => {
      setStartWebCall(true);
      setIsStartWebCall(false);
    });
    vapi.on("call-end", () => setStartWebCall(false));
    return () => vapi.removeAllListeners();
  }, [vapi]);
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const orgs = await getAllOrganisationData({ setIsLoading: () => { } });
        setOrganizations(orgs.organizations);
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    };
    fetchOrgs();
  }, []);

  const handleDelete = () => {
    setShowDeleteModal(!showDeleteModal);
    if (showDeleteModal) setIsDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!assistantId) {
      toast.error('Invalid assistant ID');
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(deleteAssistant({ assistantId })).unwrap();
      toast.success('Assistant deleted successfully!');
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error?.message || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    try {
      await dispatch(saveAssistantDetail({ assistantId, userOrgId })).unwrap();
      toast.success('Assistant updated successfully!');
    } catch (e) {
      toast.error(e?.message || 'Update failed');
    }
  };

  const startCall = (e) => {
    e.preventDefault();

    const firstMessage = activeDetail?.details?.firstMessage || '';
    const promptMessage = activeDetail?.details?.model?.messages?.[0]?.content || '';

    const extractVars = (text) =>
      (text.match(/{{\s*([\w\d_]+)\s*}}/g) || []).map((v) => v.replace(/{{\s*|\s*}}/g, ''));

    const variables = Array.from(new Set([...extractVars(firstMessage), ...extractVars(promptMessage)]));

    if (variables.length > 0) {
      setShowVariableModal(true);
    } else {
      triggerWebCall({});
    }
  };
  const triggerWebCall = async (variables) => {
    if (!vapi) {
      toast.error('Vapi client not initialized yet.');
      return;
    }
    setIsStartWebCall(true);
    try {
      await vapi.start(activeDetail?.assistant?.externalAssistantId, {
        variableValues: variables,
      });
    } catch (e) {
      console.error('Error starting call:', e);
      toast.error('Failed to start call');
      setStartWebCall(false);
      setIsStartWebCall(false);
    }
  };

  const stopCall = () => {
    if (!vapi) return;
    vapi.stop();
    setStartWebCall(false);
  };

  const handleVariableSubmit = (filledVariables) => {
    setShowVariableModal(false);
    triggerWebCall(filledVariables);
  };

  const createChatSession = async () => {
    try {
      const sessionPayload = {
        name: activeDetail?.assistant?.name || "Chat Session",
        assistantId: assistantId
      };
      
      const sessionResponse = await createSession(sessionPayload);
      setCurrentSession(sessionResponse.session);
      return sessionResponse.session;
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create chat session');
      throw error;
    }
  };

  const onSendMessage = async (messages) => {
    try {
      // Create session if it doesn't exist
      let session = currentSession;
      if (!session) {
        session = await createChatSession();
      }

      // Get the last user message
      const lastMessage = messages[messages.length - 1];
      
      // Prepare chat payload
      const chatPayload = {
        sessionId: session.id,
        input: lastMessage.content,
        assistantOverrides: {
          model: {
            provider: "google",
            model: "gemini-2.5-flash"
          }
        }
      };

      // Call chat API
      const chatResponse = await chatApi(chatPayload);
      
      // Store both user and assistant messages
      const userMessage = {
        role: 'user',
        content: lastMessage.content
      };
      
      const assistantMessage = {
        role: 'assistant',
        content: chatResponse.chat.output[0].content
      };
      
      // Update chat messages state with both messages
      setChatMessages(prev => {
        // Check if user message already exists to avoid duplicates
        const lastStoredMessage = prev[prev.length - 1];
        if (lastStoredMessage && lastStoredMessage.role === 'user' && lastStoredMessage.content === userMessage.content) {
          // User message already exists, just add assistant message
          return [...prev, assistantMessage];
        } else {
          // Add both user and assistant messages
          return [...prev, userMessage, assistantMessage];
        }
      });
      
      return assistantMessage.content;
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      throw error;
    }
  };
  const isOutOfCredits = wallet.creditsRemaining <= 0;
  const isCallActive = startWebCall || isStartWebCall;


  return (
    <div
      style={{
        width: isMobile ? '100%' : 'auto',
        margin: isMobile ? '1rem 0' : '0 auto',
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        border: '1px solid #e5e7eb',
        padding: isMobile ? '1rem 0.75rem' : '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        boxSizing: 'border-box',
        height: 'auto',
        alignSelf: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <Link
        to={`/call-logs?assistantId=${assistantId}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '0.5rem 0.75rem',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          backgroundColor: 'transparent',
          transition: 'background-color 0.2s',
          color: '#111827',
          textDecoration: 'none',
          fontWeight: '500',
          fontSize: '0.875rem',
        }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = '#f9fafb')}
        onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
      >
        <span>Call logs</span>
        <ExternalLink style={{ width: '0.875rem', height: '0.875rem' }} />
      </Link>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          borderBottom: '1px solid #e0e0e0',
          paddingBottom: '10px',
        }}
      >
        <div className='flex-1'>
          <button
            onClick={handleSave}
            type="button"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              backgroundColor: 'var( --primary-500)',
              color: 'white',
              fontWeight: '500',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontSize: '0.875rem',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = 'var( --primary-600)')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = 'var( --primary-500)')}
          >
            <Save style={{ width: '0.875rem', height: '0.875rem' }} />
            Save
          </button>
        </div>

      </div>



      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{
          width: 'fit-content',
          fontSize: '0.9rem',
          fontWeight: 550,
          color: '#111827',
        }}>
          Have a chat with your agent to test & experience it (Beta)
        </div>

        <button
          disabled={isOutOfCredits}
          type="button"
          onClick={() => {
            if (!startChat) {
              // Reset session and messages when starting new chat
              setCurrentSession(null);
              setChatMessages([]);
            }
            setStartChat(!startChat);
          }}
          style={{
            display: 'flex',
            width: '100%',
            backgroundColor: isOutOfCredits ? '#9ca3af' : 'var(--primary-500)',
            color: 'white',
            fontWeight: '500',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: isOutOfCredits ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            fontSize: '0.875rem',
            marginBottom: '10px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => {
            if (!isOutOfCredits) {
              e.target.style.backgroundColor = 'var(--primary-600)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isOutOfCredits) {
              e.target.style.backgroundColor = 'var(--primary-500)';
            }
          }}
        >
          Test your prompts via chat
        </button>
      </div>


      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div style={{
          width: 'fit-content',
          fontSize: '0.9rem',
          fontWeight: 550,
          color: '#111827',
        }}>
          Have a web call with your agent to test & experience it (Beta)
        </div>

        <button
          disabled={isOutOfCredits || isStartWebCall}
          onClick={startWebCall ? stopCall : startCall}
          type="button"
          style={{
            display: 'flex',
            width: '100%',
            backgroundColor: isOutOfCredits
              ? '#9ca3af'
              : isCallActive
                ? '#dc2626'
                : 'var(--primary-500)',
            color: 'white',
            fontWeight: '500',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: isOutOfCredits || isStartWebCall ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            fontSize: '0.875rem',
            marginBottom: '10px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => {
            if (!isOutOfCredits && !isStartWebCall) {
              e.target.style.backgroundColor = isCallActive ? '#b91c1c' : 'var(--primary-600)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isOutOfCredits && !isStartWebCall) {
              e.target.style.backgroundColor = isCallActive ? '#dc2626' : 'var(--primary-500)';
            }
          }}
        >
          {isStartWebCall ? (
            <>
              <Spinner />
              Connecting...
            </>
          ) : startWebCall ? (
            <>
              <PhoneOff style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
              End web call
            </>
          ) : (
            <>
              <Phone style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
              Start web call
            </>
          )}
        </button>
      </div>
      {startChat && (
        <ChatWidget
          onSendMessage={onSendMessage}
          onClose={() => {
            setStartChat(false);
            setCurrentSession(null);
            setChatMessages([]);
          }}
          messages={chatMessages}
        />
      )}
      {showVariableModal && (
        <WebCallVariableModal
          promptMessage={activeDetail?.details?.model?.messages?.[0]?.content || ''}
          firstMessage={activeDetail?.details?.firstMessage || ''}
          onSubmit={handleVariableSubmit}
          onCancel={() => setShowVariableModal(false)}
        />
      )}
    </div>
  );
}

RightPanel.propTypes = {
  assistantId: PropTypes.string,
};

export default RightPanel;
